import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  Stack,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";

import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Permission, Role } from "./types/permissions";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import roleService from "../../../service/roleService";
import permissionService from "../../../service/permissionService";
import { toast } from "react-toastify";
import CustomTextField from "../../../components/ui/Textfield";
import { buildPermissionAppMap, countDistinctApps } from "./permissionUtils";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";

const ACCENT_COLORS = [
  "#8B5E34",
  "#3F6C7A",
  "#7C4DFF",
  "#2E8B67",
  "#D6336C",
  "#6C3FC7",
  "#1E88E5",
  "#C77C2E",
];

const getAccentColor = (id: number) =>
  ACCENT_COLORS[Math.abs(id) % ACCENT_COLORS.length];

const RoleCard: React.FC<{
  role: Role;
  appCount: number;
  onManagePermissions: (role: Role) => void;
  onDelete: (role: Role) => void;
  canDelete: boolean;
  index: number;
}> = ({ role, appCount, onManagePermissions, onDelete, canDelete, index }) => {
  const accent = getAccentColor(role.id);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(index, 10) * 0.05 }}
      whileHover={{ y: -4 }}
    >
      <Card
        sx={{
          position: "relative",
          boxShadow: "0",
          borderRadius: 4,
          height: "100%",
          overflow: "hidden",
          background: "#ffffff",
          transition:
            "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
          "&:hover": {
            boxShadow: "0 22px 42px rgba(15, 23, 42, 0.14)",
            transform: "translateY(-4px)",
            borderColor: alpha(accent, 0.28),
          },
        }}
      >
        <CardContent sx={{ p: 2.5, pt: 2.75 }}>
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={2}
          >
            <Box minWidth={0}>
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ color: "text.primary", lineHeight: 1.2 }}
                noWrap
              >
                {role.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.75 }}
              >
                {role.is_managed
                  ? "Permissions are defined in code and refreshed on deploy."
                  : "Custom role that you can edit and remove from the UI."}
              </Typography>
            </Box>
            <Stack
              direction="row"
              spacing={0.5}
              flexShrink={0}
              alignItems="flex-start"
            >
              {role.is_managed && (
                <Tooltip title="Permissions for this role are defined in code (group_permissions.py) and reset on every deploy">
                  <Chip
                    label="Managed"
                    size="small"
                    color="warning"
                    variant="outlined"
                    icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
                  />
                </Tooltip>
              )}
              {!role.is_managed && canDelete && (
                <Tooltip title="Delete role">
                  <IconButton size="small" onClick={() => onDelete(role)}>
                    <DeleteOutlineIcon
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Box>

          <Stack direction="row" spacing={2.5} sx={{ mt: 2 }}>
            <Box display="flex" alignItems="center" gap={0.75}>
              <AppsOutlinedIcon fontSize="small" sx={{ color: accent }} />
              <Box>
                <Typography variant="body2" fontWeight={800} lineHeight={1.2}>
                  {appCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Apps
                </Typography>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={0.75}>
              <LockOutlinedIcon fontSize="small" sx={{ color: accent }} />
              <Box>
                <Typography variant="body2" fontWeight={800} lineHeight={1.2}>
                  {role.permissions.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Permissions
                </Typography>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={0.75}>
              <GroupOutlinedIcon fontSize="small" sx={{ color: accent }} />
              <Box>
                <Typography variant="body2" fontWeight={800} lineHeight={1.2}>
                  {role.user_count ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Users
                </Typography>
              </Box>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.25} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => onManagePermissions(role)}
              sx={{
                flex: 1,
                py: 1,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "0 10px 20px rgba(15, 23, 42, 0.12)",
                bgcolor: "#614338",
              }}
            >
              Manage Permissions
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </motion.div>
  );
};

type RoleFilter = "all" | "managed" | "custom";
type SortOption = "name" | "users" | "permissions";

const RolesPermissions: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canAddRole = hasPermission(PERMISSIONS.USER_MANAGEMENT_ADD);
  const canDeleteRole = hasPermission(PERMISSIONS.USER_MANAGEMENT_DELETE);
  const [roles, setRoles] = useState<Role[]>([]);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [sortBy, setSortBy] = useState<SortOption>("name");

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [rolesData, permissionsData] = await Promise.all([
        roleService.getAllRoles(),
        permissionService.getAllPermissions(),
      ]);
      setRoles(rolesData);
      setAllPermissions(permissionsData);
    } catch (err) {
      console.error("Failed to load roles", err);
      setError("Failed to load roles. Please try again.");
      toast.error("Failed to load roles", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const permissionAppMap = useMemo(
    () => buildPermissionAppMap(allPermissions),
    [allPermissions],
  );

  const filteredRoles = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return roles.filter((role) => {
      if (roleFilter === "managed" && !role.is_managed) return false;
      if (roleFilter === "custom" && role.is_managed) return false;
      if (term && !role.name.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [roles, searchTerm, roleFilter]);

  const roleCounts = useMemo(
    () => ({
      all: roles.length,
      managed: roles.filter((r) => r.is_managed).length,
      custom: roles.filter((r) => !r.is_managed).length,
    }),
    [roles],
  );

  const sortedRoles = useMemo(() => {
    const arr = [...filteredRoles];
    arr.sort((a, b) => {
      if (sortBy === "users") return (b.user_count ?? 0) - (a.user_count ?? 0);
      if (sortBy === "permissions")
        return b.permissions.length - a.permissions.length;
      return a.name.localeCompare(b.name);
    });
    return arr;
  }, [filteredRoles, sortBy]);

  const handleManagePermissions = (role: Role) => {
    navigate(`/configurations/manage-roles/view/${role.id}`);
  };

  const handleDeleteRoleClick = (role: Role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const roleCardsLoading = loading && !error;

  const handleConfirmDelete = async () => {
    if (!roleToDelete) return;

    try {
      await roleService.deleteRole(roleToDelete.id);
      setRoles((prev) => prev.filter((r) => r.id !== roleToDelete.id));
      toast.success(`Role "${roleToDelete.name}" deleted successfully`, {
        position: "top-right",
      });
    } catch (err) {
      console.error("Failed to delete role:", err);
      toast.error("Failed to delete role", { position: "top-right" });
    } finally {
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    }
  };

  if (roleCardsLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              alignItems: "center",
              justifyContent: "space-between",
            }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={loadData}
                sx={{ textTransform: "none", fontWeight: "bold" }}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          mb={3}
        >
          {/* Left Side */}
          <Stack direction="row" spacing={2} alignItems="center" flex={1}>
            <ToggleButtonGroup
              exclusive
              value={roleFilter}
              onChange={(_, value: RoleFilter | null) => {
                if (value) setRoleFilter(value);
              }}
              sx={{
                flexShrink: 0,
                "& .MuiToggleButton-root": {
                  borderRadius: 999,
                  textTransform: "none",
                  marginRight: 1,
                  px: 2,
                  py: 1,
                  borderColor: alpha(reduxTheme.primary_colors[400], 0.16),
                },
                "& .Mui-selected": {
                  color: "common.white !important",
                  backgroundColor: `${reduxTheme.primary_colors[400]} !important`,
                },
              }}
            >
              <ToggleButton value="all">All ({roleCounts.all})</ToggleButton>
              <ToggleButton value="managed">
                Managed ({roleCounts.managed})
              </ToggleButton>
              <ToggleButton value="custom">
                Custom ({roleCounts.custom})
              </ToggleButton>
            </ToggleButtonGroup>

            <CustomTextField
              placeholder="Search roles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                maxWidth: 380,
              }}
            />

            <FormControl size="small" sx={{ width: 180 }}>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                startAdornment={
                  <FilterListIcon
                    fontSize="small"
                    sx={{ mr: 1, color: "text.secondary" }}
                  />
                }
              >
                <MenuItem value="name">Name (A-Z)</MenuItem>
                <MenuItem value="users">Most Users</MenuItem>
                <MenuItem value="permissions">Most Permissions</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {/* Right Side */}
          {canAddRole && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/configurations/manage-roles/add")}
              sx={{
                backgroundColor: reduxTheme.primary_colors[400],
                borderRadius: 2,
                px: 2.5,
                py: 1.2,
                boxShadow: "0 12px 24px rgba(15, 23, 42, 0.14)",
                textTransform: "none",
                fontWeight: 700,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              Add New Role
            </Button>
          )}
        </Box>

        {sortedRoles.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              No roles found
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {sortedRoles.map((role, idx) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={role.id}>
                <RoleCard
                  role={role}
                  appCount={countDistinctApps(
                    role.permissions,
                    permissionAppMap,
                  )}
                  onManagePermissions={handleManagePermissions}
                  onDelete={handleDeleteRoleClick}
                  canDelete={canDeleteRole}
                  index={idx}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </motion.div>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the role{" "}
            <strong>&quot;{roleToDelete?.name}&quot;</strong>?
            {roleToDelete?.user_count ? (
              <>
                {" "}
                {roleToDelete.user_count} user
                {roleToDelete.user_count !== 1 ? "s" : ""} currently assigned to
                this role will lose access.
              </>
            ) : null}{" "}
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pb: 2, pr: 3 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: reduxTheme.primary_colors[400],
              borderRadius: 2,
              mr: 1,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            sx={{
              borderRadius: 2,
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesPermissions;
